import { memo, useMemo, useState } from 'react';
import { CircularProgress, Grid } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';

import DateSelectBox from './DateSelectBox';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import DoughnutChart from 'components/ui/charts/DoughnutChart';

import List from './List';

const ProductStatistics = ({ setData }) => {
  const axiosPrivate = useAxiosPrivate();
  const [searchParams] = useSearchParams();
  const [hasError, setHasError] = useState(false);

  const [startAndFinishDate, setStartAndFinishDate] = useState({
    startDate: '',
    finishDate: '',
  });
  const objectId = searchParams.get('objectId');

  const search = `${
    startAndFinishDate.startDate ? '?from=' + startAndFinishDate.startDate : ''
  }${
    startAndFinishDate.finishDate
      ? '&till=' + startAndFinishDate.finishDate
      : ''
  }`;

  const { isLoading, isFetching, isError, data } = useQuery({
    queryKey: [
      '/analytic/warehouse/topproducts',
      startAndFinishDate,
      objectId,
      search,
    ],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `/analytic/warehouse/topproducts${search}`
      );
      return response.data.data;
    },

    enabled: !hasError,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  function getRandomColor() {
    var letters = '0123456789ABCDEF';
    var color = '#';
    for (var i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  const listByAmount = useMemo(() => {
    if (data?.top_by_amount?.length) {
      const list = data?.top_by_amount?.map((p) => ({
        ...p,
        color: getRandomColor(),
      }));
      return list?.sort?.((a, b) => b?.sum - a?.sum);
    }
    return [];
  }, [data]);

  const listBySum = useMemo(() => {
    if (data?.top_by_sum?.length) {
      const list = data?.top_by_sum?.map((p) => ({
        ...p,
        color: getRandomColor(),
      }));
      return list?.sort?.((a, b) => b?.sum - a?.sum);
    }
    return [];
  }, [data]);

  return (
    <div className='residential-complex-item income-by-residential-complex !shadow-md !base-border'>
      <div className='residential-complex-item-header'>
        <div className='residential-complex-item-title font-semibold text-lg income-by-residential-complex-title'>
          Qabul qilingan mahsulotlar
        </div>
        <div className='w-[150px]'>
          <DateSelectBox
            setStartAndFinishDate={setStartAndFinishDate}
            disabled={isLoading || isFetching}
          />
        </div>
      </div>

      <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
        <Grid item={true} lg={12} md={12} sm={12} xs={12}>
          <div className='shadow-md p-2 border rounded-md w-full h-full'>
            <div className='font-semibold text-center'>
              Omborlarga mahsulotlar kirimi (UZS)
            </div>
            <div className='income-by-residential-body'>
              {isLoading || isFetching ? (
                <div className='h-[260px] flex items-center justify-center'>
                  <CircularProgress size={30} color='inherit' />
                </div>
              ) : !listByAmount?.length || isError ? (
                <div className='no-data-found-wrapper h-full'>
                  <i className='bi bi-exclamation-octagon text-lg mr-1' />{' '}
                  Ma'lumot topilmadi!
                </div>
              ) : (
                <div className='flex'>
                  <div className='income-by-residential-chart'>
                    <DoughnutChart
                      chartLabels={listByAmount?.map((p) => p?.warehouse_name)}
                      chartDatasets={[
                        {
                          data: listByAmount?.map((p) => ` ${p?.total_amount}`),
                          suffix: listByAmount?.map((p) => p?.unit),
                          backgroundColor: listByAmount?.map((p) => p?.color),
                          borderWidth: 2,
                          hoverOffset: 15,
                          hoverBorderWidth: 0,
                          borderRadius: 4,
                        },
                      ]}
                      suffix={''}
                      optionPlugins={{
                        legend: {
                          display: false,
                        },
                      }}
                      options={{
                        layout: {
                          padding: {
                            top: 10,
                            bottom: 10,
                          },
                        },
                        parsing: {
                          key: 'total',
                        },
                      }}
                    />
                  </div>
                  <List
                    list={listByAmount}
                    field='total_amount'
                    suffix='unit'
                  />
                </div>
              )}
            </div>
          </div>
        </Grid>
        <Grid item={true} lg={12} md={12} sm={12} xs={12}>
          <div className='shadow-md p-2 border rounded-md w-full h-full'>
            <div className='font-semibold text-center'>
              Omborlarga mahsulotlar kirimi ($)
            </div>
            <div className='income-by-residential-body'>
              {isLoading || isFetching ? (
                <div className='h-[260px] flex items-center justify-center'>
                  <CircularProgress size={30} color='inherit' />
                </div>
              ) : !listBySum?.length || isError ? (
                <div className='no-data-found-wrapper h-full'>
                  <i className='bi bi-exclamation-octagon text-lg mr-1' />{' '}
                  Ma'lumot topilmadi!
                </div>
              ) : (
                <div className='flex'>
                  <div className='income-by-residential-chart'>
                    <DoughnutChart
                      chartLabels={listBySum?.map((p) => p?.name)}
                      chartDatasets={[
                        {
                          data: listBySum?.map((p) => p?.total_sum_usd),
                          backgroundColor: listBySum?.map((p) => p?.color),
                          borderWidth: 2,
                          hoverOffset: 15,
                          hoverBorderWidth: 0,
                          borderRadius: 4,
                        },
                      ]}
                      suffix=' $'
                      optionPlugins={{
                        legend: {
                          display: false,
                        },
                      }}
                      options={{
                        layout: {
                          padding: {
                            top: 10,
                            bottom: 10,
                          },
                        },
                        parsing: {
                          key: 'total',
                        },
                      }}
                    />
                  </div>
                  <List list={listBySum} field='total_sum_usd' suffix=' $' />
                </div>
              )}
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default memo(ProductStatistics);
